import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet's broken default marker icons when bundled with Vite
import markerIconUrl from 'leaflet/dist/images/marker-icon.png';
import markerIcon2xUrl from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
    iconUrl: markerIconUrl,
    iconRetinaUrl: markerIcon2xUrl,
    shadowUrl: markerShadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

export interface LatLng {
    lat: number;
    lng: number;
}

interface LocationPickerProps {
    value: LatLng | null;
    radius: number;           // metres
    onChange: (latlng: LatLng) => void;
    onRadiusChange: (metres: number) => void;
}

// ── Subcomponent: handles map click events ────────────────────────────────────
function ClickHandler({ onMapClick }: { onMapClick: (latlng: LatLng) => void }) {
    useMapEvents({
        click(e) {
            onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
        },
    });
    return null;
}

// ── Subcomponent: re-centers map when value changes externally ────────────────
function MapController({ center }: { center: LatLng | null }) {
    const map = useMap();
    const prevCenter = useRef<LatLng | null>(null);

    useEffect(() => {
        if (center && center !== prevCenter.current) {
            prevCenter.current = center;
            map.setView([center.lat, center.lng], map.getZoom());
        }
    }, [center, map]);

    return null;
}

const RADIUS_MARKS = [
    { value: 50,   label: '50m' },
    { value: 250,  label: '250m' },
    { value: 500,  label: '500m' },
    { value: 1000, label: '1km' },
    { value: 2000, label: '2km' },
    { value: 5000, label: '5km' },
];

export default function LocationPicker({ value, radius, onChange, onRadiusChange }: LocationPickerProps) {
    const [search, setSearch] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');

    const defaultCenter: LatLng = value ?? { lat: 51.505, lng: -0.09 }; // London fallback

    const handleSearch = async () => {
        const q = search.trim();
        if (!q) return;
        setSearching(true);
        setSearchError('');
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`,
                { headers: { 'Accept-Language': 'en' } }
            );
            const data = await res.json();
            if (data.length > 0) {
                onChange({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
            } else {
                setSearchError('Location not found');
            }
        } catch {
            setSearchError('Search failed');
        } finally {
            setSearching(false);
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {/* Address search */}
            <TextField
                label="Search for a location"
                value={search}
                onChange={e => { setSearch(e.target.value); setSearchError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                size="small"
                fullWidth
                error={!!searchError}
                helperText={searchError || 'Search by address, place name, or coordinates — or click the map to drop a pin'}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {searching
                                ? <CircularProgress size={18} />
                                : <IconButton size="small" onClick={handleSearch} aria-label="Search"><SearchIcon fontSize="small" /></IconButton>
                            }
                        </InputAdornment>
                    ),
                }}
            />

            {/* Map */}
            <Box sx={{ height: 380, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <MapContainer
                    center={[defaultCenter.lat, defaultCenter.lng]}
                    zoom={value ? 14 : 4}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <ClickHandler onMapClick={onChange} />
                    <MapController center={value} />
                    {value && (
                        <>
                            <Marker
                                position={[value.lat, value.lng]}
                                draggable
                                eventHandlers={{
                                    dragend(e) {
                                        const m = e.target as L.Marker;
                                        const pos = m.getLatLng();
                                        onChange({ lat: pos.lat, lng: pos.lng });
                                    },
                                }}
                            />
                            <Circle
                                center={[value.lat, value.lng]}
                                radius={radius}
                                pathOptions={{ color: '#321D80', fillColor: '#321D80', fillOpacity: 0.15 }}
                            />
                        </>
                    )}
                </MapContainer>
            </Box>

            {/* Lat / Lng display */}
            {value && (
                <Box display="flex" gap={2}>
                    <TextField
                        label="Latitude"
                        value={value.lat.toFixed(6)}
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Longitude"
                        value={value.lng.toFixed(6)}
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ flex: 1 }}
                    />
                </Box>
            )}

            {!value && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Click anywhere on the map to set the badge location
                </Typography>
            )}

            {/* Radius */}
            <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Check-in radius: <strong>{radius >= 1000 ? `${radius / 1000}km` : `${radius}m`}</strong>
                    {' '}— how close a learner must be to earn this badge
                </Typography>
                <Slider
                    value={radius}
                    onChange={(_, v) => onRadiusChange(v as number)}
                    min={50}
                    max={5000}
                    step={null}
                    marks={RADIUS_MARKS}
                    valueLabelDisplay="auto"
                    valueLabelFormat={v => v >= 1000 ? `${v / 1000}km` : `${v}m`}
                    size="small"
                />
            </Box>
        </Box>
    );
}
